import { appStorage } from '../lib/safeStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserX, X, ChevronRight, AlertTriangle } from 'lucide-react';
import { notificationsAPI } from '../services/api';

/**
 * StaffDeclineAlertBanner — persistent admin alert for staff assignment declines.
 *
 * When a staffer declines their assignment (via the portal Accept/Decline
 * buttons OR the assignment-email link), the backend writes a
 * `staff_declined` in-app notification and fires a web push. This component
 * surfaces every UNREAD one as a sticky red-tinted card that NEVER
 * auto-dismisses — it stays until the admin acts:
 *   • Click "Review Staffing" → open the event workspace's Staffing tab
 *                                (notification.link is /event/{id}?section=staffing)
 *                                AND mark that notification read.
 *   • Click ✕                 → dismiss (mark read) without navigating.
 * Because "dismissed" === "marked read" on the server, the alert does not
 * reappear after a refresh once handled. Admin-only; renders nothing for
 * staff (mirroring BlockoutAlertBanner's isAdmin gate).
 */

const getRole = () => {
  try { return (JSON.parse(appStorage.getItem('user') || '{}').role) || ''; }
  catch { return ''; }
};

const StaffDeclineAlertBanner = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  // Ids the admin just acted on — kept so a poll mid-flight can't re-add
  // them before the server read-state catches up.
  const handledRef = useRef(new Set());

  const isAdmin = ['admin', 'manager'].includes(getRole());

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await notificationsAPI.getAll(20, true, 'staff_declined');
      const items = data?.notifications || (Array.isArray(data) ? data : []);
      setAlerts(items.filter((n) => !handledRef.current.has(n.id || n._id)));
    } catch {
      /* silent — transient poll failures shouldn't disrupt the dashboard */
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAlerts();
    const id = setInterval(fetchAlerts, 60000);
    return () => clearInterval(id);
  }, [isAdmin, fetchAlerts]);

  const handle = useCallback(async (n, review) => {
    const id = n.id || n._id;
    handledRef.current.add(id);
    setAlerts((prev) => prev.filter((a) => (a.id || a._id) !== id));
    try { await notificationsAPI.markAsRead(id); } catch { /* best effort */ }
    if (review && n.link) navigate(n.link);
  }, [navigate]);

  if (!isAdmin || alerts.length === 0) return null;

  const shown = alerts.slice(0, 3);
  const extra = alerts.length - shown.length;

  return (
    <div
      className="fixed z-[9998] top-[76px] right-4 left-4 sm:left-auto sm:w-[400px] flex flex-col gap-2"
      data-testid="staff-decline-banner"
    >
      {shown.map((n) => {
        const md = n.metadata || {};
        const staffName = md.staffName || 'A staff member';
        const eventLabel = md.eventLabel || 'this event';
        return (
          <div
            key={n.id || n._id}
            role="alert"
            data-testid="staff-decline-row"
            className="rounded-2xl border border-red-500/35 bg-[#160a0d]/95 backdrop-blur-xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-all animate-in fade-in slide-in-from-top-2 duration-300"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <UserX className="w-4 h-4 text-red-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-red-400/85 font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" />
                  Staff Declined
                </p>
                <p className="text-sm text-white font-medium mt-0.5 truncate">
                  <span className="text-red-300">{staffName}</span>
                  <span className="text-white/70"> declined — </span>
                  <span className="text-white">{eventLabel}</span>
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => handle(n, true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold text-[#0B0F1A] transition-all hover:opacity-90 active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, #E6C86A, #C5A028)',
                      boxShadow: '0 6px 16px -6px rgba(212,175,55,0.55)',
                    }}
                    data-testid="staff-decline-review-btn"
                  >
                    Review Staffing
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <button
                onClick={() => handle(n, false)}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Dismiss alert"
                data-testid="staff-decline-dismiss-btn"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        );
      })}
      {extra > 0 && (
        <button
          onClick={() => navigate('/notifications')}
          className="self-end text-xs text-gray-300 hover:text-white px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 backdrop-blur-xl"
          data-testid="staff-decline-more"
        >
          +{extra} more decline{extra > 1 ? 's' : ''} — view all
        </button>
      )}
    </div>
  );
};

export default StaffDeclineAlertBanner;
